import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import './GamePage.css';

// Hooks
import { useGameState } from './game/hooks/useGameState';
import { useGameTimer } from './game/hooks/useGameTimer';
import { useChat } from './game/hooks/useChat';
import { useBoardInteraction } from './game/hooks/useBoardInteraction';
import { useGameActions } from './game/hooks/useGameActions';
import { useVoting } from './game/hooks/useVoting';
import { useSoundEffects } from './game/hooks/useSoundEffects';

// Components
import WaitingRoom from './game/WaitingRoom';
import TeamChat from './game/TeamChat';
import ChessBoardPanel from './game/ChessBoard';
import AdminPanel from './game/AdminPanel';
import KingActions from './game/KingActions';
import PlayerBanner from './game/PlayerBanner';
import TimerWarning from './game/TimerWarning';
import VotingPanel from './game/VotingPanel';

// Utils
import { isKingRole, getTimerWarningLevel, getTeamMembersWithPieces } from './game/utils/gameUtils';

const GamePage = () => {
  const { gameId } = useParams();
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();

  // --- Hooks ---
  const {
    game, loading, error, playerData, playerTeam,
    boardOrientation, chessPosition, setChessPosition, chess,
  } = useGameState(gameId, currentUser, isAdmin);

  const chat = useChat(gameId, game, currentUser, isAdmin, playerData, playerTeam);

  // Voting hook (needs startTimer, defined below via lazy ref)
  const startTimerRef = React.useRef(null);

  const voting = useVoting(
    gameId, game, chess, currentUser, isAdmin, playerData, playerTeam,
    setChessPosition, (...args) => startTimerRef.current?.(...args)
  );

  const timer = useGameTimer(
    gameId, game, playerTeam, isAdmin, voting.handleTimerExpiration
  );

  // Wire up the lazy ref so voting can call startTimer
  startTimerRef.current = timer.startTimer;

  const board = useBoardInteraction(
    gameId, game, chess, currentUser, isAdmin, playerData, playerTeam,
    setChessPosition, timer.startTimer
  );

  const actions = useGameActions(
    gameId, game, chess, currentUser, isAdmin, playerData, playerTeam, setChessPosition
  );

  // Sound effects driven by shared game state (moves, game start/end)
  const sound = useSoundEffects(game);

  // Clear selections when game state resets
  useEffect(() => {
    if (game?.status && game.status !== 'in_progress') {
      board.clearSelections();
      voting.setSelectedMoves([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.status]);

  // --- Derived state ---
  const hasKingRole = isKingRole(playerData?.role);
  const isEliminated = game?.eliminatedPlayers?.includes(currentUser?.email) || false;

  const timerData = playerTeam === 'white' ? game?.turnTimer?.white : game?.turnTimer?.black;
  const warningLevel = (
    !isAdmin && playerTeam && game?.status === 'in_progress' && game?.currentTurn === playerTeam
  ) ? getTimerWarningLevel(timer.remainingTime, timerData?.phase) : null;

  const handleBackToDashboard = () => {
    navigate(isAdmin ? '/admin' : '/game');
  };

  // --- Render: Loading ---
  if (loading) {
    return (
      <div className="game-container">
        <div className="game-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading game...</p>
          </div>
        </div>
      </div>
    );
  }

  // --- Render: Error ---
  if (error) {
    return (
      <div className="game-container">
        <div className="game-content">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h2>Error</h2>
            <p className="error-text">{error}</p>
            <button onClick={handleBackToDashboard} className="back-button">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Render: Waiting Room ---
  if (game.status === 'waiting') {
    return (
      <WaitingRoom
        game={game}
        currentUser={currentUser}
        generalMessages={chat.messages.general}
        generalMessageInput={chat.generalMessageInput}
        setGeneralMessageInput={chat.setGeneralMessageInput}
        handleSendGeneralMessage={chat.handleSendGeneralMessage}
        sending={chat.sending}
        generalChatRef={chat.generalChatRef}
        onBackToDashboard={handleBackToDashboard}
      />
    );
  }

  // --- Render: Joining ---
  if (!isAdmin && !playerTeam && game) {
    return (
      <div className="game-container">
        <div className="game-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Joining game...</p>
            <p className="joining-hint">If this takes too long, try going back to your dashboard.</p>
            <button onClick={handleBackToDashboard} className="back-button" style={{ marginTop: '20px' }}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Render: Active Game ---
  // Admin has independent inputs for each team chat.
  const handleAdminInputChange = (team) => (val) => {
    const text = typeof val === 'string' ? val : (val?.target?.value ?? '');
    if (team === 'white') chat.setWhiteAdminInput(text);
    else chat.setBlackAdminInput(text);
  };

  return (
    <div className="game-container">
      <Header />

      <div className="game-content-active">
        {/* Team Indicator */}
        {!isAdmin && playerTeam && (
          <div className={`team-indicator ${playerTeam}-team-indicator`}>
            <div className="team-indicator-icon">
              {playerTeam === 'white' ? '♔' : '♚'}
            </div>
            <div className="team-indicator-text">
              <h2>You are playing on the {playerTeam === 'white' ? 'WHITE' : 'BLACK'} TEAM</h2>
              <p>{playerData?.nickname}</p>
            </div>
          </div>
        )}

        {/* Paused notice (players) */}
        {!isAdmin && playerTeam && game.status === 'in_progress' && game.paused && (
          <div className="game-paused-banner">
            ⏸ The game is paused by the host. Please wait — it will resume shortly.
          </div>
        )}

        {/* Player Banner */}
        <PlayerBanner
          game={game}
          playerData={playerData}
          playerTeam={playerTeam}
          isEliminated={isEliminated}
        />

        {/* King Actions */}
        {hasKingRole && game.status === 'in_progress' && !isEliminated && (
          <KingActions
            game={game}
            hasPendingDrawOffer={actions.hasPendingDrawOffer()}
            madeDrawOffer={actions.madeDrawOffer()}
            offerDraw={actions.offerDraw}
            acceptDraw={actions.acceptDraw}
            declineDraw={actions.declineDraw}
            resign={actions.resign}
          />
        )}

        {/* Timer Warning */}
        <TimerWarning warningLevel={warningLevel} remainingTime={timer.remainingTime} />

        <div className={`game-layout ${isAdmin ? 'admin-layout' : 'player-layout'}`}>
          {/* Admin: White Team Chat */}
          {isAdmin && (
            <TeamChat
              team="white"
              messages={chat.messages.white}
              chatTab={chat.chatTab}
              setChatTab={chat.setChatTab}
              messageInput={chat.whiteAdminInput}
              setMessageInput={handleAdminInputChange('white')}
              onSendMessage={chat.handleSendMessage}
              sending={chat.sending}
              currentUserEmail={currentUser.email}
              isAdmin={true}
              chatRef={chat.whiteChatRef}
            />
          )}

          {/* Player: Own Team Chat */}
          {!isAdmin && playerTeam && (
            <TeamChat
              team={playerTeam}
              messages={playerTeam === 'white' ? chat.messages.white : chat.messages.black}
              chatTab={chat.chatTab}
              setChatTab={chat.setChatTab}
              messageInput={chat.messageInput}
              setMessageInput={(val) => {
                if (typeof val === 'string') chat.setMessageInput(val);
                else chat.setMessageInput(val.target ? val.target.value : val);
              }}
              onSendMessage={chat.handleSendMessage}
              sending={chat.sending}
              currentUserEmail={currentUser.email}
              isAdmin={false}
              chatRef={playerTeam === 'white' ? chat.whiteChatRef : chat.blackChatRef}
              inputRef={chat.chatInputRef}
            />
          )}

          {/* Chess Board */}
          <ChessBoardPanel
            game={game}
            chess={chess}
            chessPosition={chessPosition}
            boardOrientation={boardOrientation}
            isAdmin={isAdmin}
            playerTeam={playerTeam}
            remainingTime={timer.remainingTime}
            optionSquares={board.optionSquares}
            previewedMove={board.previewedMove}
            hoveredSquare={board.hoveredSquare}
            hoverPos={board.hoverPos}
            getPieceName={board.getPieceName}
            onDrop={board.onDrop}
            onSquareClick={board.onSquareClick}
            boardWrapperRef={board.boardWrapperRef}
            onBoardMouseMove={(e) => board.handleBoardMouseMove(e, boardOrientation)}
            onBoardMouseLeave={board.handleBoardMouseLeave}
            revertLastMove={actions.revertLastMove}
            soundMuted={sound.muted}
            onToggleSound={sound.toggleSound}
          />

          {/* Admin: Black Team Chat */}
          {isAdmin && (
            <TeamChat
              team="black"
              messages={chat.messages.black}
              chatTab={chat.chatTab}
              setChatTab={chat.setChatTab}
              messageInput={chat.blackAdminInput}
              setMessageInput={handleAdminInputChange('black')}
              onSendMessage={chat.handleSendMessage}
              sending={chat.sending}
              currentUserEmail={currentUser.email}
              isAdmin={true}
              chatRef={chat.blackChatRef}
            />
          )}

          {/* Admin Panel */}
          {isAdmin && <AdminPanel game={game} />}

          {/* Player: Right Sidebar (Team Members + Voting) */}
          {!isAdmin && playerTeam && (
            <div className="right-sidebar">
              {/* Team Members */}
              <div className="team-members-panel">
                <h4 className="team-members-title">
                  {playerTeam === 'white' ? '♔' : '♚'} Team Members
                </h4>
                <div className="team-members-list">
                  {getTeamMembersWithPieces(game, playerTeam, currentUser?.email).map((member, index) => (
                    <div
                      key={index}
                      className={`team-member-item ${member.isEliminated ? 'eliminated' : ''} ${member.isCurrentUser ? 'current-user' : ''}`}
                    >
                      <span className="member-piece">{member.pieceSymbol}</span>
                      <span className="member-name">{member.nickname}</span>
                      {member.isEliminated && <span className="member-status">☠️</span>}
                      {member.isCurrentUser && <span className="member-you">(You)</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Voting Panel */}
              <VotingPanel
                game={game}
                playerTeam={playerTeam}
                playerData={playerData}
                currentUserEmail={currentUser.email}
                selectedMoves={voting.selectedMoves}
                setSelectedMoves={voting.setSelectedMoves}
                createPoll={voting.createPoll}
                voteOnMove={voting.voteOnMove}
                executeWinningMove={voting.executeWinningMove}
                setPreviewedMove={board.setPreviewedMove}
              />
            </div>
          )}
        </div>

        <button onClick={handleBackToDashboard} className="back-button-game">
          Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default GamePage;
